import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { isSeparateDisplayControllerAtom, isDistanceControlOpenAtom, isAreaCalcControlOpenAtom, isDrawControlOpenAtom, isDesktopAtom, searchResultLngLatAtom, fiwareDataAtom, menuItemsAtom, dataLoadedResultAtom, sidebarHeightAtom, isTerrainControlerOpenAtom, clickLngLatAtom, selectedFeatureAtom, tOSViewStateAtom } from '../../atoms';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import './Map.css';
import { useToggleLayerControl, useMeasureDistanceControl, useDrawControl, useTerrainControl, useSeparateDisplay, useCenterMarkControl } from '../../hooks/';
import MessagePanel from '../messagePanel/MessagePanel';
import { useMeasureAreaControl } from '../../hooks/useMeasureAreaControl'; 
import MapColorPicker from '../colorpicker/ColorPicker';
import { addMapController } from '../../lib/mapController';
import { MAIN_MAP_ID, SECOND_MAP_ID } from '../../constants/components/map';
import { anyType, fiwareDataItemType, MapPropsType } from '../../types';
import MapStyleControlPanel from '../mapStyleControlPanel/MapStyleControlPanel';
import maplibregl from 'maplibre-gl';
import { putMarker } from '../../lib/markerControl';
import { useSearchParams } from 'react-router-dom';
import mapStyleConfig from '../../assets/mapStyleConfig.json';
import { useGetTileUrls, useGetTileUrlsByType } from '../../lib/useGetTileUrls';
import { DataType } from '../../types/lib/types';
import { TileUrlInfo, UrlsDataType } from '../../types/lib/useGetTileUrls';
import { mapLayerControl } from '../../lib/mapLayerControl';
import { clearResult } from '../../lib/objectHighlight';
import ErrorPanel from '../errorPanel/ErrorPanel';
import MapDataContainer from '../mapDataContainer/MapDataContainer';
import IndexMap from '../indexMap/IndexMap';
import ReactGA from 'react-ga4';
import { MapStyleConfigType } from '../../types/components/mapStyleConfig';
import { changeStyle } from '../../lib/mapUtils';
import MAP_STYLE from '../../assets/style.json';
import { MapTestSupporter } from '../MapTestSupporter';


declare global {
  interface Window {
    geolonia: {
      Popup: new (options?: maplibregl.PopupOptions) => maplibregl.Popup;
      Map: new (options: maplibregl.MapOptions) => maplibregl.Map;
      GeolocateControl: new (options: maplibregl.GeolocateControlOptions) => maplibregl.GeolocateControl;
      NavigationControl: new (options?: maplibregl.NavigationControlOptions) => maplibregl.NavigationControl;
      Marker: new (options?: maplibregl.MarkerOptions) => maplibregl.Marker;
    };
    map?: maplibregl.Map; // Extend Window to include the map property
  }
}

type ConfType = 'basemap' | 'overlay';

type HasZoomConfItemType = {
  id: string;
  type: ConfType;
  minzoom?: number;
  maxzoom?: number;
};

// const ZOOM_CONF_TYPE = {
//   'basemap': '背景地図',
//   'overlay': 'データ'
// }


const Map: React.FC<MapPropsType> = ({ 
  mapObj, 
  setMapObj,
  mapType, 
  appSettings, 
  selectedBaseMap, 
  setSelectedBaseMap, 
  selectedLayers, 
  clickEvent, 
  setClickEvent, 
  dataErrorMessage, 
  setDataErrorMessage,
  nonLngLatDataArr, 
  setNonLngLatDataArr,
  isIndexMapOpen,
  setIsIndexMapOpen,
  isCenterMarkControlOpen,
  setIsCenterMarkControlOpen
}) => {

  const [drawColor, setDrawColor] = useState({ r: 255, g: 0, b: 0, a: 1 });                         // 作図の色
  const [mapClickEvent, setMapClickEvent] = useState<maplibregl.MapLayerMouseEvent | undefined>();   // マップクリックイベント
  const [isMoveEnd, setIsMoveEnd] = useState<boolean>(false);                                       // 移動終了フラグ
  const [searchParams, setSearchParams] = useSearchParams();                                        // URLパラメータ

  // Note: urlからのパラメーターが更新されない事があるため、上書き用のref
  const selectedLayersStrRef = useRef<string>('');    
  const isSetClickEvent = useRef<boolean>(true);                                                    // クリックイベントを設定するかどうか
  const mapContainer = useRef<HTMLDivElement>(null);
  const hasMapControl = useRef<boolean>(false);                                                     // マップコントロールの有無
  const markerRef = useRef<maplibregl.Marker | undefined>(undefined);                               // マーカーオブジェクト

  // atom
  const isDesktop = useAtomValue(isDesktopAtom);                                                    // PC or SP
  const [searchResultLngLat, setSearchResultLngLat] = useAtom(searchResultLngLatAtom);              // 検索結果の緯度経度
  const [dataLoadedResult, setIsDataLoaded] = useAtom(dataLoadedResultAtom);                        // データ読み込み完了フラグ
  const setSidebarHeight = useSetAtom(sidebarHeightAtom);                                           // サイドバー開け閉め（スマホ）
  const [lngLat, setLngLat] = useAtom(clickLngLatAtom);
  const tOSViewState = useAtomValue(tOSViewStateAtom);

  // flag
  const isSeparateDisplayControl = useAtomValue(isSeparateDisplayControllerAtom);                         // 2画面
  const isDrawControlOpen = useAtomValue(isDrawControlOpenAtom);                                          // 描く
  const [isDistanceControlOpen, setIsDistanceControlOpen] = useAtom(isDistanceControlOpenAtom);           // 距離計測
  const [isAreaCalcControlOpen, setIsAreaCalcControlOpen] = useAtom(isAreaCalcControlOpenAtom);           // 面積計測
  const setIsTerrainControlerOpen = useSetAtom(isTerrainControlerOpenAtom);                               // 3D表示

  // hooks
  const distanceLabel = useMeasureDistanceControl(mapObj, isDistanceControlOpen === mapType);       // 距離計測ラベル
  const areaLabel = useMeasureAreaControl(mapObj, isAreaCalcControlOpen === mapType);               // 面積計測ラベル

  // data
  const fiwareData = useAtomValue(fiwareDataAtom);
  const menuItems = useAtomValue(menuItemsAtom);
  const selectedFeatures = useAtomValue(selectedFeatureAtom);

  const fiawreCatalogItems = useMemo(() =>
      menuItems ? menuItems.filter((item) => item.dataType === 'fiware') as unknown as fiwareDataItemType[] : []
      , [menuItems]);
  const dataPngUrls = useGetTileUrlsByType('datapng');
  const rasterUrls = useGetTileUrlsByType('raster');
  const vectorTileUrls = useGetTileUrlsByType('vector');
  const tileUrls = useGetTileUrls();

  // ズームレベルの指定を持ってるレイヤー・選択中の背景図を取得
  const hasZoomConfItems: HasZoomConfItemType[] | undefined = useMemo(() => {

    const items = menuItems?.filter(
        (item) => (item.metadata?.minzoom || item.metadata?.maxzoom) && selectedLayers?.includes(item.id)
      ).map((item) => {
        return {
          id: item.id,
          type: 'overlay',
          minzoom: item.metadata?.minzoom,
          maxzoom: item.metadata?.maxzoom
        } as HasZoomConfItemType;
      });

    if(selectedBaseMap?.minzoom || selectedBaseMap?.maxzoom) {
      items?.push({
        id: selectedBaseMap.label,
        type: 'basemap',
        minzoom: selectedBaseMap.minzoom,
        maxzoom: selectedBaseMap.maxzoom
      });
    }

    return items;
  } , [menuItems, selectedLayers, selectedBaseMap]);


  /* ****************** 
   * URLパラメータのセット
   * ******************/
  const setURLParameter = () => {
    setIsMoveEnd(true);
  };

  /* ****************** 
   * ズームの規制
   * ******************/
  const zoomControlHandler = () => {
    if (!hasZoomConfItems || !mapObj) { return; }

    // minzoomの最大値を取得
    const minZoom = hasZoomConfItems
      .filter((item) => item && 'minzoom' in item)
      .reduce((item: HasZoomConfItemType | undefined, currentItem) => {
        if (!currentItem || currentItem.minzoom === undefined) { return item; }
        return (item && item.minzoom && currentItem.minzoom < item.minzoom )? item : currentItem;
    }, undefined);

    const maxZoom = hasZoomConfItems
      .filter((item) => item && 'maxzoom' in item)
      .reduce((item: HasZoomConfItemType | undefined, currentItem) => {
        if (!currentItem || currentItem.maxzoom === undefined) { return item; }
        return (item && item.maxzoom && currentItem.maxzoom > item.maxzoom )? item : currentItem;
      }, undefined);

    // ここでminZoom/maxZoomを計算
    const minZoomValue = minZoom?.minzoom ?? appSettings.minZoom;
    const maxZoomValue = maxZoom?.maxzoom ?? appSettings.maxZoom;

    mapObj.setMinZoom(minZoomValue);
    mapObj.setMaxZoom(maxZoomValue);

  };

  // マップクリック時の処理
  const onClickMap = (e: maplibregl.MapMouseEvent) => {
    setMapClickEvent(e);
  };

  // マップクリック時の処理
  useEffect(() => {
    if (!mapObj || !mapClickEvent || isDrawControlOpen === mapType || isDistanceControlOpen === mapType || isAreaCalcControlOpen === mapType) { return; }

    // 位置情報が表示されている場合、クリックイベントを無視
    if (!isSetClickEvent.current) {
      isSetClickEvent.current = true;
      setClickEvent(undefined);
      setLngLat(undefined);
      const features = selectedFeatures ? Object.values(selectedFeatures).flat() : undefined;
      clearResult(mapObj, features);
      setSidebarHeight('close');        // 地図をクリックしたらメニューを閉じる（スマホ）
      return; 
    }

    setClickEvent(mapClickEvent);

    if (Array.isArray(mapClickEvent.lngLat)) {
      setLngLat(mapClickEvent.lngLat as unknown as [number, number]);
    } else {
      setLngLat([mapClickEvent.lngLat.lng, mapClickEvent.lngLat.lat]);
    }

    //【GoogleAnalytics】画像出力ボタン クリックトラッキング
    ReactGA.event({
      category: "lngLat_click", // カテゴリ（任意の名前）
      action: "click",          // イベントの種類
      label: `座標：${mapClickEvent.lngLat.lng},${mapClickEvent.lngLat.lat}`,       // ボタン名
    });

    // 現在のズームが 10以下の場合 flyTo を実行しない
    if (mapObj.getZoom() <= 10) { return; }

    mapObj.flyTo({ 
      center: mapClickEvent.lngLat,
      duration: 200,
      essential: true
    });

    setURLParameter();

  // 関数を依存に入れると、関数が変わる度に再実行されてしまうため、入れない
  // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [mapClickEvent]);


  /* *******************
   * ソースとレイヤーの追加
   * *******************/
  const handleMapLayerControl = async (
    map: maplibregl.Map, 
    dataPngUrls: UrlsDataType[], 
    fiawreCatalogItems: fiwareDataItemType[], 
    fiwareData: { [key: string]: anyType }, 
    rasterUrls: UrlsDataType[], 
    tileUrls: TileUrlInfo[], 
    vectorTileUrls: UrlsDataType[], 
    menuItems: DataType[],
    baseMapSources: MapStyleConfigType[]
  ) => {
    if(mapType === MAIN_MAP_ID && typeof dataLoadedResult === 'object' && dataLoadedResult.result === 'success') { return; }

    const result = await mapLayerControl(
      map, 
      { 
        dataPngUrls, 
        fiawreCatalogItems, 
        fiwareData, 
        rasterUrls, 
        tileUrls, 
        vectorTileUrls, 
        menuItems,
        baseMapSources
      }
    );

    if(typeof result === 'object' && result.result === 'error') { return; }
    
    // 緯度経度のないデータをセット
    // setNonLngLatDataArr((result as anyType).nonLngLatDataArr);
    // TODO Need to fix correctly
    if (!(result as anyType).nonLngLatDataArr) {
      setNonLngLatDataArr([{title: '', data: [], isShow: false}])
    } else {
      setNonLngLatDataArr((result as anyType).nonLngLatDataArr);
    }

    if (mapType === SECOND_MAP_ID) { return; }

    setIsDataLoaded(result);
  };

  // mapの読み込みを開始する
  useEffect(() => {
    if(tOSViewState === 'firstclosed' && mapObj && fiwareData && menuItems) {
      // 背景図のソースを取得
      const filtered = mapStyleConfig.filter(style =>
        style.sources.some(source => typeof source === 'object')
      ).map(style => style).flat();

      // ソース・レイヤーの追加、結果をセット（error or success）
      handleMapLayerControl(mapObj, dataPngUrls, fiawreCatalogItems, fiwareData, rasterUrls, tileUrls, vectorTileUrls, menuItems, filtered);
      
    }
  }, [tOSViewState, mapObj, fiwareData, menuItems])


  // fiwareDataでデータを更新
  useEffect(() => {
    if(!fiwareData || !mapObj || !mapObj.getStyle().sources) { return; }
    Object.keys(fiwareData).forEach((sourceId: string) => {
      const source = mapObj.getSource(sourceId);

      if(!source) { return; }

      const targetItem = fiawreCatalogItems.find(item => item.dataId === sourceId);

      // fiwareの場合、tileIdがある場合（ポリゴン系のデータ）は飛ばす
      if(targetItem && 'tileId' in targetItem) { return; }
      
      // アラートの更新
      if (targetItem && targetItem.metadata?.dataType === 'Alert') { 
        if(!nonLngLatDataArr) { return; }

        const newNonLngLatData = [...nonLngLatDataArr];
        const targetIndex = newNonLngLatData.findIndex(data => data.title === targetItem.id);

        if(targetIndex === -1) { return; }
        newNonLngLatData[targetIndex].data = Array.isArray(fiwareData[sourceId]) ? fiwareData[sourceId] : [fiwareData[sourceId]];
        setNonLngLatDataArr(newNonLngLatData);

        return;
      }
      
      // データを更新
      (source as maplibregl.GeoJSONSource).setData(fiwareData[sourceId]);
    });
  } , [fiwareData, mapObj, fiawreCatalogItems]);


  /* ****************** 
   * URLパラメータを取得
   * ******************/
  useEffect(() => {
    selectedLayersStrRef.current = searchParams.get('layers')??'';
  }, [searchParams]);



  /* ****************** 
   * 初期設定
   * ******************/
  useEffect(() => {
    if (mapObj || !dataPngUrls || !fiawreCatalogItems || !fiwareData || !rasterUrls || !tileUrls || !vectorTileUrls || !menuItems) {
      return;
    }

    const map: maplibregl.Map = new window.geolonia.Map({
      container: mapContainer.current!,
      style: MAP_STYLE as maplibregl.StyleSpecification,
      center: appSettings.center as [number, number],
      // 意図せず傾き・回転を変更してしまうことを防ぐ
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      maxRotate: 0,
      pitch: appSettings.pitch,
      zoom: appSettings.zoom,
      minZoom: appSettings.minZoom,
      maxZoom: appSettings.maxZoom,
      localIdeographFontFamily: 'sans-serif'
    });

    // Playwright のテストのために window.map に mapオブジェクトをセット
    window.map = map;

    // 挙動おかしくなるので、ダブルクリックズームを無効化
    map.doubleClickZoom.disable();

    // ロード時の処理
    map.on('load', () => {
      if (hasMapControl.current) { return; }

      /* 地図のコントロールを追加 */
      addMapController(
        map,
        isDesktop,
        () => setIsIndexMapOpen(), 
        () => setIsTerrainControlerOpen((prev: boolean) => !prev),
        () => setIsCenterMarkControlOpen()
      );

      hasMapControl.current = true;

      setMapObj(map);
    });

    // スプライトの読み込み状態を追跡
    const loadingSprites = new Set<string>();

    map.on('styleimagemissing', (e: anyType) => {
      const spriteId = e.id;
      
      // GSIスプライト（gsi:で始まる）はスプライトシートに含まれているためスキップ
      // 既に読み込み中または読み込み済みの場合スキップ
      if (spriteId.startsWith('gsi:') || (loadingSprites.has(spriteId) || map.hasImage(spriteId))) return;

      // 読み込み開始を記録
      loadingSprites.add(spriteId);

      const url = `${location.protocol}//${location.host}${location.pathname}sprites/${spriteId}.png`;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      map.loadImage(url, (error: anyType, image: anyType) => {
        // 読み込み完了後、状態を更新
        loadingSprites.delete(spriteId);

        if (error) {
          console.error(`Failed to load sprite ${spriteId}:`, error);
          return;
        }
        if (image && !map.hasImage(spriteId)) {
          map.addImage(spriteId, image);
        }
      });
    });

    // 移動処理
    map.on('moveend', setURLParameter);
    map.on('click', onClickMap);

  // 関数を依存に入れると、挙動がおかしくなる為入れない
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSettings, isDesktop, dataPngUrls, fiawreCatalogItems, fiwareData, rasterUrls, tileUrls, vectorTileUrls, menuItems]);


  /* ****************** 
   * ズームの規制を設定
   * ******************/
  useEffect(() => {
    if (!mapObj || !hasZoomConfItems) { return; }

    // 初回表示時にズームを規制
    zoomControlHandler();
    
    mapObj.on('zoom', zoomControlHandler);

    return () => {
      mapObj.off('zoom', zoomControlHandler);
    };
  }, [mapObj, hasZoomConfItems]);


  /* ******************
   * 検索結果地点にクリックイベントを生成
   * ******************/
  useEffect(() => {
    if(!searchResultLngLat || !mapObj) { return; }
    if(markerRef.current) {
      markerRef.current?.remove();
      markerRef.current = undefined;
      isSetClickEvent.current = true;
    }

    onClickMap({
      type: 'click',
      lngLat: searchResultLngLat as unknown as maplibregl.LngLat,
      point: mapObj.project(searchResultLngLat),
      target: mapObj,
      originalEvent: new MouseEvent('click')
    } as maplibregl.MapMouseEvent);
    
    setSearchResultLngLat(undefined);
    
  // 関数を依存に入れると、挙動がおかしくなる為入れない
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapObj, searchResultLngLat]);  


  /* ********************
   * 背景図を切り替え
   * ********************/
  useEffect(() => {
    if (!mapObj || !selectedBaseMap) return;

    // スタイルが読み込まれたら背景図を再度切り替える
    const onStyleData = () => {
      changeStyle(mapObj, selectedBaseMap.sources, mapStyleConfig);
    };

    mapObj.on('styledata', onStyleData);

    // 初回も一度実行
    changeStyle(mapObj, selectedBaseMap.sources, mapStyleConfig);

    return () => {
      mapObj.off('styledata', onStyleData);
    };
  }, [mapObj, selectedBaseMap]);


  /* ********************
   * URLパラメータをセット
   * ********************/
  useEffect(() => {
    if (!mapObj || !isMoveEnd) { return; }

    const center = mapObj.getCenter();
    const zoom = mapObj.getZoom();
    const pitch = mapObj.getPitch();

    setSearchParams((prev) => {
      if (center) {
        const centerValue = Array.isArray(center) ? center : Object.values(center);
        if (prev.get('center') !== centerValue.join('/')) {
          prev.set('center', centerValue.join('/'));
        }
      }
      if(zoom && prev.get('zoom') !== zoom.toString()) { 
        prev.set('zoom', zoom.toString()); 
      }
      if(pitch && prev.get('pitch') !== pitch.toString()) { 
        prev.set('pitch', pitch.toString()); 
      }
      return prev;
    });

    setIsMoveEnd(false);

  // setSearchParamsを入れると挙動がおかしくなるため入れない
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMoveEnd, mapObj]);


  /* ******************
   * マーカーをセット
   * ******************/
  useEffect(() => {
    if(!mapObj) { return; }
    
    if(!lngLat) {
      markerRef.current?.remove();
      markerRef.current = undefined;
      isSetClickEvent.current = true;
      return; 
    }
    
    if(markerRef.current) {
      markerRef.current.setLngLat(lngLat);
      return;
    }
    
    markerRef.current = putMarker(mapObj, lngLat);
    isSetClickEvent.current = false;

  }, [lngLat, mapObj]);

  /* ****************** 
   * マップのレイヤー周りのコントロール
   * ******************/
  useToggleLayerControl(mapObj, selectedLayers, mapType, setDataErrorMessage, nonLngLatDataArr, setNonLngLatDataArr, setClickEvent);

  /* ****************** 
   * 作図周りのコントロール
   * ******************/
  useDrawControl(mapObj, drawColor, isDrawControlOpen === mapType);

  /* ****************** 
   * 中心地点マークのコントロール
   * ******************/
  useCenterMarkControl(mapObj, isCenterMarkControlOpen);

  /* ****************** 
   * 3D表示のコントロール
   * ******************/
  useTerrainControl(mapObj);

  /* ****************** 
   * 2画面表示のコントロール
   * ******************/
  useSeparateDisplay(mapObj, mapType);

  return (
    <div className={classNames('map-wrapper', {
      'right-map': mapType === MAIN_MAP_ID && isSeparateDisplayControl,
      'left-map': mapType === SECOND_MAP_ID && isSeparateDisplayControl
    })}>
      <div className='map-inner'>
        {/* Map */}
        <div
          className='map'
          ref={mapContainer}
          data-lang="ja"
          data-gesture-handling="off"
          data-navigation-control="off"
          data-scale-control="on"
        >
        </div>

        <div className='nowZoom'>
          <p className='small-text'>ズームレベル: {mapObj && parseFloat(mapObj.getZoom().toFixed(1))}</p>
        </div>


        {/* 索引図 */}
        { isIndexMapOpen &&
          <IndexMap mainMap={mapObj} /> 
        }

        {/* 緯度経度なしのデータを表示するパネル */}
        <MapDataContainer 
          mapClickEvent={clickEvent}
          map={mapObj}
          setMapClickEvent={setClickEvent}
          nonLngLatDataArr={nonLngLatDataArr}
          mapType={mapType}
        />
        
        {/* 背景地図選択 */}
        <MapStyleControlPanel
          selectedBaseMap={selectedBaseMap} 
          setSelectedBaseMap={setSelectedBaseMap} 
          position='right'
        />

        {/* エラーメッセージパネル */}
        <ErrorPanel
          dataErrorMessage={dataErrorMessage}
          setDataErrorMessage={setDataErrorMessage}
        />

        {/* 距離計測のパネル */}
        { distanceLabel &&
            <MessagePanel
              message={distanceLabel}
              position={isDesktop ? 'top-center' : 'bottom-center'}
              closeHandler={() => {
                setIsDistanceControlOpen(undefined);
                setSidebarHeight('close');
              }}
            />
        }

        {/* 面積計測のパネル */}
        { areaLabel &&
            <MessagePanel
              message={areaLabel}
              position={isDesktop ? 'top-center' : 'bottom-center'}
              closeHandler={() => {
                setIsAreaCalcControlOpen(undefined);
                setSidebarHeight('close');
              }}
            />
        }

        {/* カラーピッカー */}
        { isDrawControlOpen === mapType &&
            <MapColorPicker
              color={drawColor}
              setColor={setDrawColor}
            />
        }

        {/* e2eテスト用のMapTestSupporter */}
        <MapTestSupporter 
          map={mapObj} 
          identifier={mapType === MAIN_MAP_ID ? "main" : "sub"} 
        />
      </div>
    </div>
  );
};

export default Map;
